import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import IconAddLayer from '../../../resources/add_layer.png';
import { Box, Divider, Icon, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import { AddCircleOutline as Add, ExpandMore as ExpandIcon } from '@material-ui/icons';

import LayerViewer from './LayerViewer';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import { formatDate } from '../../../common/utils';

const useStyles = makeStyles((theme) => ({
  title: {
    wordBreak: 'break-word',
  },
}));

/**
 * @function ImageViewer
 * @description One collapsible entry per acquired image, listing the visualization layers built
 * from it. An image with no layers is an ordinary empty state - the user adds layers through the
 * button below - so it says so, rather than the "Carregando" it used to show: nothing is being
 * fetched at that point, and the wording made a finished panel look stuck mid-load.
 * @param {Object} props
 * @param {Object} props.image - The image whose layers to list.
 * @param {Number|String} props.index - The image's key in the imagery store.
 * @param {Boolean} [props.withExpression=true] - Whether to offer the "new layer" action.
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const ImageViewer = ({ image, index, withExpression = true }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();

  const hasLayers = image && image.layers && Object.keys(image.layers).length > 0;

  return (
    <Accordion defaultExpanded style={{ margin: 0, width: '100%' }}>
      <AccordionSummary expandIcon={<ExpandIcon />} style={{ minHeight: 12, margin: 0 }}>
        <Typography className={classes.title} variant="body1">
          {
            // Get mission short name by splitting its name
            typeof image == 'object' ? image.name.split('/')[0] + ' - ' + formatDate(image.date, true) : ''
          }
        </Typography>
      </AccordionSummary>
      <Divider />
      <AccordionDetails style={{ padding: '8px 16px' }}>
        <Box width="100%" display="flex" alignItems="center" flexDirection="column">
          {!hasLayers && (
            <Typography variant="caption" color="textSecondary" align="center">
              {t('forms.imageryOverlay.noLayers')}
            </Typography>
          )}
          {hasLayers &&
            Object.keys(image.layers)
              .reverse()
              .map((id, i) => <LayerViewer key={i} layer={image.layers[id]} index={id} parent={index} />)}
        </Box>
      </AccordionDetails>
      {withExpression && (
        <>
          <Divider />
          <Tooltip title={t('forms.imageryOverlay.hint')} placement="top">
            <IconButton onClick={() => dispatch(Imagery.requestExpression(index))} style={{ padding: '9px' }}>
              <img src={IconAddLayer} alt="Icon Add LayerIcon" style={{ width: 30, height: 30 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Accordion>
  );
};

export default ImageViewer;
